class ContentOverview extends HTMLElement {
  #scrollSpyObserver = null;
  #linkMap = new Map();

  connectedCallback() {
    this.#bindElements();
    this.#buildToc();
    this.#bindSmoothScroll();
    this.#bindScrollSpy();
  }

  disconnectedCallback() {
    this.#scrollSpyObserver?.disconnect();
  }

  #bindElements() {
    this.articleContent = document.querySelector(".js-article-content");
    this.tocList = this.querySelector(".js-toc-list");

    const contentHeadings = this.articleContent
      ? Array.from(this.articleContent.querySelectorAll("h2, h3"))
      : [];

    // Instruction titles live outside .js-article-content (rendered by
    // the static `_sewing-instructions` block). They are picked up here
    // so the TOC reflects the full document outline.
    const instructionHeadings = Array.from(
      document.querySelectorAll(".sewing-instructions [data-toc-instruction]"),
    );

    this.contentHeadingCount = contentHeadings.length + instructionHeadings.length;

    const titleEl = document.querySelector(".js-article-title");
    this.headings = [
      ...(titleEl ? [titleEl] : []),
      ...contentHeadings,
      ...instructionHeadings,
    ];
  }

  #buildToc() {
    if (!this.tocList || this.contentHeadingCount < 3) return;
    this.removeAttribute("hidden");
    this.#setInitialOpenState();
    this.tocList.innerHTML = "";
    this.#linkMap.clear();

    this.headings.forEach((heading, index) => {
      if (!heading.id) heading.id = `heading-${index}`;

      const li = document.createElement("li");
      if (heading.hasAttribute("data-toc-instruction")) {
        li.classList.add("is-instruction");
      }

      const a = document.createElement("a");
      a.href = `#${heading.id}`;
      a.textContent = heading.textContent;

      li.appendChild(a);
      this.tocList.appendChild(li);
      this.#linkMap.set(heading.id, li);
    });
  }

  #setInitialOpenState() {
    const details = this.querySelector("details");
    if (!details) return;
    if (window.matchMedia("(min-width: 768px)").matches) {
      details.setAttribute("open", "");
    }
  }

  #setActive(li) {
    if (!this.tocList) return;
    this.tocList.querySelectorAll("li.is-active").forEach((el) => {
      if (el !== li) el.classList.remove("is-active");
    });
    if (li) li.classList.add("is-active");
  }

  #bindSmoothScroll() {
    if (!this.tocList) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.tocList.addEventListener("click", (event) => {
      const link = event.target.closest("a[href^='#']");
      if (!link) return;
      const id = decodeURIComponent(link.getAttribute("href").slice(1));
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      this.#setActive(this.#linkMap.get(id));
      target.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start",
      });
      if (history.replaceState) {
        history.replaceState(null, "", `#${id}`);
      }
    });
  }

  #bindScrollSpy() {
    if (!this.tocList || this.#linkMap.size === 0) return;
    if (typeof IntersectionObserver === "undefined") return;

    const visible = new Set();
    const getTopOffset = () => {
      const headerHeight =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue("--header-height"),
          10
        ) || 0;
      return headerHeight + 16;
    };

    this.#scrollSpyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        if (visible.size === 0) return;
        const top = [...visible].sort(
          (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
        )[0];
        this.#setActive(this.#linkMap.get(top.id));
      },
      {
        rootMargin: `-${getTopOffset()}px 0% -65% 0%`,
        threshold: 0,
      }
    );

    this.headings.forEach((h) => this.#scrollSpyObserver.observe(h));
  }
}

customElements.define("content-overview", ContentOverview);
